import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, X } from 'lucide-react';
import GameBoard from '@/components/GameBoard';
import PlayingCard from '@/components/PlayingCard';
import Scoreboard from '@/components/Scoreboard';
import TutorialPrimer from '@/components/TutorialPrimer';
import CoachMark from '@/components/CoachMark';
import HelpGlossary, { JokerExplainerBody, DoubleChanceExplainerBody } from '@/components/HelpGlossary';
import DevPanel from '@/components/DevPanel';
import JokerSequence from '@/components/JokerSequence';
import ShotSequence from '@/components/ShotSequence';
import DoubleChanceSequence from '@/components/DoubleChanceSequence';
import PitchBackground from '@/components/PitchBackground';
import MidfieldDrawBall from '@/components/MidfieldDrawBall';
import MatchStatusBar from '@/components/MatchStatusBar';
import HalftimeOverlay from '@/components/HalftimeOverlay';
import PenaltyView from './PenaltyView';
import MatchSummaryView from './MatchSummaryView';
import { useGameEngine } from '@/hooks/useGameEngine';
import { useTurnTimer } from '@/hooks/useTurnTimer';
import { useDeferredScores } from '@/hooks/useDeferredScores';
import { chooseBotAction, chooseBotPenaltyAction, BOT_DELAY_MS, BOT_DECIDE_DELAY_MS } from '@/engine/bot';
import { selectors } from '@/engine/engine';
import { validPlacements, validAttackTargets, hasAnyValidAction } from '@/engine/rules';
import { TID } from '@/constants/testIds';
import { storage } from '@/lib/storage';
import { sounds } from '@/audio/sounds';
import { COACH_MARKS } from '@/lib/tutorialConfig';
import { useScreenWakeLock } from '@/hooks/useScreenWakeLock';

const TURN_MS = 15000;
const KICKOFF_MS = 1500;
const TURN_PASS_HOLD_MS = 800;       // brief freeze before next player resumes after a goal/save
const NO_MOVE_VISIBLE_MS = 1500;     // how long the "NO VALID MOVE" label is shown when human can't act
const NO_MOVE_SKIP_FLOOR_MS = 500;   // minimum visibility when the human taps to skip the no-move state
// Final-reveal guards (2026-02-13). End-of-match transitions MUST wait for the
// last visual reveal to finish so the player sees their final goal/save.
const PENALTY_REVEAL_HOLD_MS = 2400; // penalty card-comparison frame stays visible before summary (Phase 3.8.2)
const MATCH_END_REVEAL_FLOOR_MS = 1000; // never skip below this even on tap

const guidedSteps = [
  { text: 'Tap DRAW in the center to draw a card.' },
  { text: 'Place into a GREEN slot or attack a GREEN opponent card.' },
  { text: 'A GOAL or SAVE ends your turn. Otherwise you keep drawing.' },
];

export default function GameScreen() {
  const navigate = useNavigate();
  // Phase 3.5.5 closed-alpha tutorial.
  // Primer auto-opens on first Bot Match (one-shot — until skipped or completed).
  const [showPrimer, setShowPrimer] = useState(() => {
    const s = storage.getSettings();
    return !s.tutorialPrimerSeen && !s.tutorialSkipped;
  });
  // Help glossary (HELP button in match HUD).
  const [showHelp, setShowHelp] = useState(false);
  // Single-active coach mark. Setting null dismisses the current one.
  const [activeCoach, setActiveCoach] = useState(null);
  // Joker / DC explainer overlays (one-shot per session). These reuse the
  // glossary explainer bodies to keep copy 100% consistent.
  const [showJokerExplainer, setShowJokerExplainer] = useState(false);
  const [showDcExplainer, setShowDcExplainer] = useState(false);
  // Track which coach marks have already fired THIS match so we never repeat.
  const shownCoachRef = useRef(new Set());
  // Global skip for the entire tutorial session (set via "SKIP TUTORIAL").
  const [tutorialSkipped, setTutorialSkipped] = useState(() => storage.getSettings().tutorialSkipped);
  const [showKickoff, setShowKickoff] = useState(true);
  const [showDev, setShowDev] = useState(false);
  const [devReveal, setDevReveal] = useState(false);
  const [devEnabled, setDevEnabled] = useState(() => storage.getSettings().devEnabled);
  const [jokerSeq, setJokerSeq] = useState(null);
  const [shotSeq, setShotSeq] = useState(null);
  const [dcSeq, setDcSeq] = useState(null);
  /**
   * Phase 3.8.2.1 — Inline no-valid-move discard flash. Holds the just-drawn
   * card that had no legal move for ~800 ms so it briefly renders in the
   * normal drawn-card slot (same size, same position as a live draw). No
   * modal, no dark backdrop — the card simply appears where a real draw
   * would appear, then vanishes and moves to the discard pile.
   */
  const [discardFlash, setDiscardFlash] = useState(null);
  /** Time at which any blocking animation (joker/turn-pass) is allowed to release. */
  const [holdUntil, setHoldUntil] = useState(0);
  const [holdNow, setHoldNow] = useState(0);
  /**
   * Final-reveal guard for penalty shootout (2026-02-13). When a PENALTY_REVEAL
   * event fires, hold the PenaltyView visible until this timestamp so the
   * player sees the deciding card comparison BEFORE the summary screen takes
   * over. Independent of `holdUntil` because penalty reveals don't render
   * through the joker/shot/dc overlays.
   */
  const [penaltyRevealUntil, setPenaltyRevealUntil] = useState(0);
  const [guidedStep, setGuidedStep] = useState(-1);

  const { state, dispatch, reset, lastEvents } = useGameEngine({ p2IsBot: true });

  // Phase 3.8 — keep screen awake during active gameplay only. Released
  // on match-end / penalty completion / exit / unmount. User can disable
  // via Settings → "Keep screen awake during matches".
  const wakeLockEnabled =
    storage.getSettings().keepScreenAwake !== false &&
    state.phase !== 'match-end' &&
    state.phase !== 'finished';
  useScreenWakeLock(wakeLockEnabled);

  useEffect(() => {
    setDevEnabled(storage.getSettings().devEnabled);
  }, [showPrimer]);

  // Auto-close DevPanel when entering halftime or match-end so its z-50 overlay
  // doesn't sit on top of the halftime / match-summary screens.
  useEffect(() => {
    if (state.phase === 'halftime' || state.phase === 'match-end' || state.phase === 'finished') {
      setShowDev(false);
    }
  }, [state.phase]);

  // KICKOFF overlay
  useEffect(() => {
    setShowKickoff(true);
    const t = setTimeout(() => setShowKickoff(false), KICKOFF_MS);
    return () => clearTimeout(t);
  }, [state.matchId]);

  // Tick holdNow every 100ms WHILE holdUntil OR penaltyRevealUntil is in the
  // future; auto-stop after both expire. The same ticker drives blocking
  // animations AND the penalty-reveal final-frame guard.
  useEffect(() => {
    const horizon = Math.max(holdUntil, penaltyRevealUntil);
    if (horizon <= Date.now()) return;
    const id = setInterval(() => {
      const now = Date.now();
      setHoldNow(now);
      if (now >= horizon) clearInterval(id);
    }, 100);
    return () => clearInterval(id);
  }, [holdUntil, penaltyRevealUntil]);

  // Pending reveal flags (2026-02-13 final-action-reveal fix). End-of-match
  // and end-of-penalty transitions MUST wait for these.
  const pendingBoardReveal = jokerSeq !== null || shotSeq !== null || dcSeq !== null;
  const pendingPenaltyReveal = Date.now() < penaltyRevealUntil;

  const isBlocked = pendingBoardReveal || Date.now() < holdUntil;

  // P0 anti-spoiler — defer scoreboard updates until the reveal sequence
  // completes (or the post-turn hold expires). Backend/engine scoring is
  // unchanged; this only buffers the displayed score.
  const displayScores = useDeferredScores(
    state.players?.p1?.score ?? 0,
    state.players?.p2?.score ?? 0,
    pendingBoardReveal || holdNow < holdUntil
  );

  // ----- EVENT HANDLERS -----
  // Toasts have been intentionally removed (player-facing card animations now
  // carry the same information visually). The engine events feed only:
  //   • the Joker reveal sequence
  //   • the Shot reveal sequence
  //   • the post-turn-pass blocker (so the bot doesn't immediately steal focus)
  useEffect(() => {
    if (!lastEvents || lastEvents.length === 0) return;
    const jokerEv = lastEvents.find((e) => e.type === 'JOKER');
    const goalEv = lastEvents.find((e) => e.type === 'GOAL');
    const saveEv = lastEvents.find((e) => e.type === 'SAVE');
    const turnPassEv = lastEvents.find((e) => e.type === 'TURN_PASSED');
    const dcAtkEv = lastEvents.find((e) => e.type === 'JOKER_DOUBLE_ATTACK_RESULT');
    const dcDefEv = lastEvents.find((e) => e.type === 'JOKER_DC_DEFENSE_RESULT');

    if (jokerEv) {
      const outcome = goalEv ? 'goal' : saveEv ? 'save' : 'goal';
      const gk = goalEv?.gk ?? saveEv?.gk ?? null;
      // Phase 3.8.2 — Joker SHOOT vs Double-Chance Goalkeeper reveal fix.
      // When the follow-up shot lands on a DC GK, the engine emits either
      // GOAL or SAVE with `dcDraws` (up to 2 defensive cards). Route to
      // DcSequence 'defense' mode so the player sees:
      //   shooter shot card  vs  DC GK  +  DC defensive draw(s)
      // instead of the plain "joker → outcome" reveal that hid the DC draws.
      const outcomeEv = goalEv || saveEv;
      if (outcomeEv && outcomeEv.dcDraws && outcomeEv.dcDraws.length > 0 && outcomeEv.gk) {
        const isGoal = outcomeEv.type === 'GOAL';
        const humanIsShooter = (isGoal ? outcomeEv.scorer : (outcomeEv.defender === 'p1' ? 'p2' : 'p1')) === 'p1';
        const positive = humanIsShooter ? isGoal : !isGoal;
        setDcSeq({
          mode: 'defense',
          primaryCard: outcomeEv.shooter,
          targetCard: outcomeEv.gk,
          draws: outcomeEv.dcDraws,
          resultPositive: positive,
          resultLabel: isGoal ? 'GOAL!' : 'SAVE!',
        });
      } else {
        setJokerSeq({ jokerCard: jokerEv.jokerCard, followUp: jokerEv.followUp, gk, outcome });
      }
    } else if (goalEv || saveEv) {
      const ev = goalEv || saveEv;
      // Double-Chance GK shot — route to the DC sequence for the 1-2 draw reveal.
      if (ev.dcDraws && ev.dcDraws.length > 0 && ev.gk) {
        const isGoal = ev.type === 'GOAL';
        const humanIsShooter = (isGoal ? ev.scorer : (ev.defender === 'p1' ? 'p2' : 'p1')) === 'p1';
        // resultPositive from p1 POV: shooter scored a goal → positive if human
        // is shooter; otherwise positive if human saved.
        const positive = humanIsShooter ? isGoal : !isGoal;
        setDcSeq({
          mode: 'defense',
          primaryCard: ev.shooter,
          targetCard: ev.gk,
          draws: ev.dcDraws,
          resultPositive: positive,
          resultLabel: isGoal ? 'GOAL!' : 'SAVE!',
        });
      } else {
        setShotSeq({
          shooter: ev.shooter,
          gk: ev.gk,
          outcome: goalEv ? 'goal' : 'save',
        });
      }
    }

    if (dcAtkEv && dcAtkEv.type === 'JOKER_DOUBLE_ATTACK_RESULT') {
      setDcSeq({
        mode: 'attack',
        primaryCard: dcAtkEv.jokerCard,
        targetCard: dcAtkEv.targetCard,
        draws: dcAtkEv.draws,
        attackerDraws: dcAtkEv.attackerDraws,
        defenderDraws: dcAtkEv.defenderDraws,
        resultPositive: dcAtkEv.success,
        resultLabel: dcAtkEv.success ? 'TARGET REMOVED' : 'TARGET SURVIVED',
      });
    } else if (dcDefEv && dcDefEv.type === 'JOKER_DC_DEFENSE_RESULT') {
      // Result is "positive" from the DEFENDER's POV when blocked. From the
      // HUMAN player's POV: positive when their side benefits — i.e. when
      // they were the defender and blocked, OR when they were the attacker
      // and the DC was removed. Compute relative to p1 (the human).
      const humanIsDefender = dcDefEv.defenderId === 'p1';
      const positive = humanIsDefender ? dcDefEv.blocked : !dcDefEv.blocked;
      setDcSeq({
        mode: 'defense',
        primaryCard: dcDefEv.attacker,
        targetCard: dcDefEv.dcCard,
        draws: dcDefEv.draws,
        resultPositive: positive,
        resultLabel: dcDefEv.blocked ? 'BLOCKED' : 'DC REMOVED',
      });
    }

    if (turnPassEv) {
      setHoldUntil((h) => Math.max(h, Date.now() + TURN_PASS_HOLD_MS));
    }

    // Phase 3.8.2.1 — Reveal auto-discarded card INLINE at the normal
    // drawn-card position for ~800 ms. Reverted from the Phase 3.8.2
    // centered modal (visually too heavy). No overlay, no backdrop —
    // just the card in the same spot it would appear on a live draw.
    const drawDiscardedEv = lastEvents.find((e) => e.type === 'DRAW_DISCARDED');
    if (drawDiscardedEv && drawDiscardedEv.card) {
      setDiscardFlash({
        id: `dsc-${state.tick}-${Date.now()}`,
        card: drawDiscardedEv.card,
        byP1: drawDiscardedEv.player === 'p1',
      });
    }

    // Final-reveal guard for penalty shootout (2026-02-13). Every PENALTY_REVEAL
    // — including the deciding sudden-death kick — must keep the PenaltyView
    // mounted with `lastShot` visible for at least PENALTY_REVEAL_HOLD_MS so
    // the result is seen BEFORE the match-summary screen replaces it.
    const penaltyRevealEv = lastEvents.find((e) => e.type === 'PENALTY_REVEAL');
    if (penaltyRevealEv) {
      setPenaltyRevealUntil((p) => Math.max(p, Date.now() + PENALTY_REVEAL_HOLD_MS));
    }
  }, [lastEvents]);

  // ----- TUTORIAL COACH-MARK ENGINE (closed-alpha) -----
  // Fires natural, non-blocking marks during the first Bot Match. Skipped
  // wholesale if the user tapped "SKIP TUTORIAL" or has already completed
  // their first bot match. Capped at MAX_AUTO_COACH per match — the HELP
  // button is always available for deeper reference.
  const MAX_AUTO_COACH = 4;
  const autoCoachCountRef = useRef(0);
  const fireCoach = (id) => {
    if (tutorialSkipped) return;
    if (storage.getSettings().tutorialFirstBotMatchCompleted) return;
    if (shownCoachRef.current.has(id)) return;
    if (autoCoachCountRef.current >= MAX_AUTO_COACH) return;
    if (activeCoach) return; // never queue — single visible mark at a time
    shownCoachRef.current.add(id);
    autoCoachCountRef.current += 1;
    setActiveCoach(COACH_MARKS[id]);
  };

  // Trigger 1: first turn — fired BEFORE the player draws. Copy points
  // them at the DRAW button (centre of board) and explains the drawn
  // card is their action card.
  useEffect(() => {
    if (!showPrimer && state.phase === 'turn-start' && state.currentPlayer === 'p1' && !state.drawnCard) {
      fireCoach('firstTurn');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.currentPlayer, showPrimer]);

  // Trigger 2: after-draw — fired the moment the player has a drawn
  // card in hand on their turn. Explains green = valid options.
  useEffect(() => {
    if (!showPrimer && state.drawnCard && state.currentPlayer === 'p1') {
      fireCoach('afterDraw');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.drawnCard, state.currentPlayer, showPrimer]);

  // Trigger 3: branch — ATTACK / BUILD FORMATION coach mark after the
  // player's first attack OR first placement. Also surface the unlock
  // milestones (defenseOpen, goalkeeperOpen) as opponent lines thin.
  useEffect(() => {
    if (!lastEvents) return;
    const attackEv = lastEvents.find((e) => e.type === 'ATTACK_RESULT' && (e.attacker === 'p1' || e.attackerId === 'p1' || e.from === 'p1'));
    const placeEv = lastEvents.find((e) => e.type === 'CARD_PLACED' && e.player === 'p1');
    const blockEv = lastEvents.find((e) => e.type === 'ATTACK_RESULT' && (e.outcome === 'loss' || e.outcome === 'blocked' || e.outcome === 'tie') && (e.attacker === 'p1' || e.attackerId === 'p1' || e.from === 'p1'));
    const gkRevealEv = lastEvents.find((e) => (e.type === 'GOAL' || e.type === 'SAVE') && e.gk);
    const jokerEv = lastEvents.find((e) => e.type === 'JOKER' && e.player === 'p1');
    const dcChoiceEv = lastEvents.find((e) => (e.type === 'JOKER_DOUBLE_CHOOSE' || e.type === 'JOKER_DOUBLE_ATTACK_RESULT') && (e.player === 'p1'));
    if (attackEv) fireCoach('branchAttack');
    if (placeEv) fireCoach('branchPlace');
    if (blockEv) fireCoach('firstBlock');
    if (gkRevealEv) fireCoach('firstGoalkeeper');

    if (jokerEv && !storage.getSettings().tutorialFirstBotMatchCompleted && !tutorialSkipped) {
      if (!shownCoachRef.current.has('jokerExplainer')) {
        shownCoachRef.current.add('jokerExplainer');
        setShowJokerExplainer(true);
      }
    }
    if (dcChoiceEv && !storage.getSettings().tutorialFirstBotMatchCompleted && !tutorialSkipped) {
      if (!shownCoachRef.current.has('dcExplainer')) {
        shownCoachRef.current.add('dcExplainer');
        setShowDcExplainer(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEvents]);

  // Trigger 4: path-to-goal unlock milestones — surface "DEFENSE OPEN"
  // the moment opponent ATTACK line drops to 1 remaining, and
  // "GOALKEEPER OPEN" the moment opponent DEFENSE line drops to 1.
  // Counts are derived from state.players.p2 so this is purely a render
  // effect; no gameplay logic touched.
  useEffect(() => {
    if (showPrimer || tutorialSkipped) return;
    if (state.phase === 'penalty' || state.phase === 'finished' || state.phase === 'match-end') return;
    const oppAtkCount = (state.players?.p2?.attacks || []).filter(Boolean).length;
    const oppDefCount = (state.players?.p2?.defenses || []).filter(Boolean).length;
    if (oppAtkCount === 1) fireCoach('defenseOpen');
    if (oppDefCount === 1 && oppAtkCount <= 1) fireCoach('goalkeeperOpen');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.players?.p2?.attacks, state.players?.p2?.defenses, state.phase, showPrimer, tutorialSkipped]);

  // Trigger 5: penalties — show on phase transition.
  useEffect(() => {
    if (state.phase === 'penalty') fireCoach('firstPenalties');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  // Mark first bot match completed once we reach a terminal phase.
  useEffect(() => {
    if (state.phase === 'finished' || state.phase === 'match-end') {
      const s = storage.getSettings();
      if (!s.tutorialFirstBotMatchCompleted) {
        storage.setSettings({ tutorialFirstBotMatchCompleted: true });
      }
    }
  }, [state.phase]);


  // Player auto-discard: if the human has drawn a card with no valid action,
  // show "NO VALID MOVE" for NO_MOVE_VISIBLE_MS and then auto-pass turn. The
  // dispatcher is held in noMoveFireRef so the tap-anywhere skip can shorten
  // the wait (still respecting NO_MOVE_SKIP_FLOOR_MS for readability).
  const noMoveFireRef = useRef(null);
  const noMoveStartedAtRef = useRef(0);
  const isHumanStuck =
    state.phase === 'drawn' &&
    state.currentPlayer === 'p1' &&
    !!state.drawnCard &&
    !hasAnyValidAction(state);

  useEffect(() => {
    noMoveFireRef.current = null;
    if (!isHumanStuck) return;
    if (isBlocked) return;
    noMoveStartedAtRef.current = Date.now();
    let dispatched = false;
    const fire = () => {
      if (dispatched) return;
      dispatched = true;
      dispatch({ type: 'END_TURN' });
    };
    const t = setTimeout(fire, NO_MOVE_VISIBLE_MS);
    noMoveFireRef.current = fire;
    return () => {
      clearTimeout(t);
      noMoveFireRef.current = null;
    };
  }, [isHumanStuck, isBlocked, state.drawnCard?.id]);

  const onSkipNoMove = () => {
    if (!noMoveFireRef.current) return;
    const elapsed = Date.now() - noMoveStartedAtRef.current;
    if (elapsed >= NO_MOVE_SKIP_FLOOR_MS) {
      noMoveFireRef.current();
      return;
    }
    const wait = NO_MOVE_SKIP_FLOOR_MS - elapsed;
    const fire = noMoveFireRef.current;
    setTimeout(() => fire && fire(), wait);
  };

  // Bot card hidden-reveal flag — when the bot draws, the central hub keeps the
  // card face-DOWN until the bot's chosen action reveals it. Placement into the
  // GOALKEEPER slot must NEVER reveal the card to the human (Correction 1).
  //
  // Joker V2 hidden-info fix (2026-02-13): a drawn JOKER must ALSO stay hidden
  // until the bot's action naturally exposes it (SHOOT, DC ATTACK, DC PLACE
  // atk/def). If the bot places DC as goalkeeper, the joker must remain
  // hidden — otherwise the human could infer the GK is a Double Chance card
  // just from seeing a joker animation.
  const [botCardRevealed, setBotCardRevealed] = useState(false);
  useEffect(() => {
    if (state.lastDrawnBy === 'p2' && state.drawnCard) {
      setBotCardRevealed(false);
    } else if (!state.drawnCard) {
      setBotCardRevealed(false);
    }
  }, [state.drawnCard?.id, state.lastDrawnBy]);

  // Bot driver — continuous turn, paused during joker/holdUntil.
  // Exposes a "fire now" callback in a ref so the tap-catcher overlay can
  // shorten the bot's thinking delay on user request.
  //
  // Hidden-information policy (Correction 1):
  //   • When the bot draws, the human sees only the card BACK.
  //   • Before dispatching the bot's action, we reveal the card ONLY if the
  //     resulting action would naturally expose it:
  //       - PLACE attack/defense → reveal (~350 ms pause) then dispatch.
  //       - ATTACK              → reveal (~350 ms pause) then dispatch (the
  //                                shot sequence then plays).
  //       - END_TURN (no move)  → reveal (~350 ms pause) then dispatch
  //                                (drawn card briefly flashes before discard).
  //       - PLACE goalkeeper    → DO NOT reveal. Dispatch immediately and the
  //                                card moves silently into the hidden GK slot.
  const botFireNowRef = useRef(null);
  useEffect(() => {
    botFireNowRef.current = null;
    if (showKickoff) return;
    if (isBlocked) return;
    if (state.currentPlayer !== 'p2') return;
    if (state.phase === 'match-end' || state.phase === 'finished' || state.phase === 'penalty') return;
    if (state.phase === 'halftime') return;
    // Pacing per phase.
    let delay;
    if (state.phase === 'turn-start') delay = BOT_DELAY_MS;
    else if (state.phase === 'drawn') delay = BOT_DECIDE_DELAY_MS;
    else if (state.phase === 'joker-choice') delay = BOT_DECIDE_DELAY_MS;
    else if (state.phase === 'joker-double-action') delay = BOT_DECIDE_DELAY_MS;
    else return;
    let dispatched = false;
    let revealTimer = null;
    const fire = () => {
      if (dispatched) return;
      dispatched = true;
      // turn-start: no drawn card yet — just dispatch DRAW.
      if (state.phase === 'turn-start') {
        dispatch(chooseBotAction(state));
        return;
      }
      // Hidden-info reveal logic (Correction 1 + 2026-02-13 Joker fix).
      // The bot's drawn card stays face-down by default. Reveal only when the
      // about-to-be-dispatched action would naturally expose the card:
      //   • PLACE atk/def       → reveal then dispatch
      //   • ATTACK              → reveal then dispatch (shot sequence follows)
      //   • PLACE GK            → DO NOT reveal — silently moves to hidden slot
      //   • JOKER_SHOOT         → reveal then dispatch (JokerSequence shows it)
      //   • JOKER_DOUBLE_CHOOSE → DO NOT reveal — keeps the joker hidden while
      //                            the bot decides ATTACK vs PLACE
      //   • JOKER_DOUBLE_ATTACK → reveal then dispatch (DC sequence shows it)
      //   • JOKER_DOUBLE_PLACE atk/def → reveal then dispatch (public slot)
      //   • JOKER_DOUBLE_PLACE GK      → DO NOT reveal (hidden DC GK)
      //   • END_TURN            → reveal then dispatch (brief flash before discard)
      const action = chooseBotAction(state);
      const hidesCard =
        (action.type === 'PLACE' && action.slotType === 'goalkeeper') ||
        action.type === 'JOKER_DOUBLE_CHOOSE' ||
        (action.type === 'JOKER_DOUBLE_PLACE' && action.slotType === 'goalkeeper');
      if (hidesCard) {
        dispatch(action);
        return;
      }
      setBotCardRevealed(true);
      revealTimer = setTimeout(() => dispatch(action), 350);
    };
    const t = setTimeout(fire, delay);
    botFireNowRef.current = fire;
    return () => {
      clearTimeout(t);
      if (revealTimer) clearTimeout(revealTimer);
      botFireNowRef.current = null;
    };
  }, [state.currentPlayer, state.phase, state.turnNumber, state.tick, showKickoff, isBlocked, holdNow]);

  // Tap-anywhere-to-skip bot delay. Active during bot's "thinking" phases:
  // turn-start, drawn, joker-choice, joker-double-action.
  const canSkipBotDelay =
    !showKickoff &&
    !isBlocked &&
    state.currentPlayer === 'p2' &&
    (state.phase === 'turn-start' ||
     state.phase === 'drawn' ||
     state.phase === 'joker-choice' ||
     state.phase === 'joker-double-action');

  // Combined skip: covers bot thinking OR the human "no valid move" wait. Both
  // hand off to a single tap-catcher overlay so the player can speed up either
  // pause from anywhere on the screen.
  const canTapToSkip = canSkipBotDelay || (isHumanStuck && !isBlocked);

  const onTapSkip = () => {
    if (botFireNowRef.current) { botFireNowRef.current(); return; }
    if (noMoveFireRef.current) { onSkipNoMove(); return; }
  };

  // PHASE 4.3.1B — Simultaneous strategic penalty bot driver.
  // The bot fires exactly ONE action per penalty decision:
  //   • when bot (p2) is the shooter   → PENALTY_PICK_ATTACK
  //   • when bot (p2) is the defender  → PENALTY_PICK_DEFENSE
  // Reveal fires automatically in the engine once both roles have
  // locked. The bot dispatches ONLY if its role isn't already locked
  // — the effect is idempotent across React renders.
  const penaltyBotSkipRef = useRef(null);
  useEffect(() => {
    penaltyBotSkipRef.current = null;
    if (state.phase !== 'penalty' || !state.penalty) return;
    if (state.penalty.finished) return;
    // Bot role — always p2 in local practice.
    const botIsShooter = state.penalty.currentShooter === 'p2';
    const botRole = botIsShooter ? 'shooter' : 'defender';
    const botLock = state.penalty.locks?.[botRole];
    if (botLock?.locked) return;  // already locked this decision
    const action = chooseBotPenaltyAction(state, 'p2');
    if (!action) return;
    let cancelled = false;
    let resolver = null;
    const skipNext = () => { if (resolver) { const r = resolver; resolver = null; r(); } };
    penaltyBotSkipRef.current = skipNext;
    (async () => {
      // Small delay so the human perceives the "opponent decision"
      // moment. When bot is defender, we deliberately wait a bit
      // longer so the shooter feels their pick is truly hidden until
      // they've picked.
      const delay = botIsShooter ? BOT_DELAY_MS : 600;
      await new Promise((res) => {
        resolver = res;
        setTimeout(() => { resolver = null; res(); }, delay);
      });
      if (cancelled) return;
      dispatch(action);
      penaltyBotSkipRef.current = null;
    })();
    return () => {
      cancelled = true;
      penaltyBotSkipRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.penalty?.currentShooter, state.penalty?.shots?.length,
       state.penalty?.locks?.shooter?.locked, state.penalty?.locks?.defender?.locked]);

  // Penalty tap-skip overlay visible only when the bot is thinking AND
  // the human's own role has already locked — i.e. we're waiting on the
  // bot alone. PHASE 4.3.1D BUGFIX: the previous condition
  // (`currentShooter === 'p2'`) rendered a full-screen invisible tap-
  // catcher whenever the bot was shooter, which physically blocked the
  // human DEFENDER's own goalkeeper card clicks. The bot no longer
  // picks the opponent's cards — the human defender must be able to
  // click their own hand freely. We now only show the overlay when the
  // human has done their part and the bot is the ONLY remaining actor.
  const humanRole = state.penalty?.currentShooter === 'p1' ? 'shooter' : 'defender';
  const humanRoleLocked = !!state.penalty?.locks?.[humanRole]?.locked;
  const botRole = humanRole === 'shooter' ? 'defender' : 'shooter';
  const botRoleLocked = !!state.penalty?.locks?.[botRole]?.locked;
  const canSkipPenaltyBot =
    state.phase === 'penalty' &&
    !!state.penalty &&
    !state.penalty.finished &&
    humanRoleLocked &&
    !botRoleLocked;
  const onTapSkipPenalty = () => {
    if (penaltyBotSkipRef.current) penaltyBotSkipRef.current();
  };

  // Turn timer — paused during joker/turn-pass/kickoff
  const isHumanTurn = state.currentPlayer === 'p1';
  const inBoardPhase = state.phase === 'turn-start' || state.phase === 'drawn';
  const timerActive = !showKickoff && !isBlocked && isHumanTurn && inBoardPhase;
  const remaining = useTurnTimer({
    durationMs: TURN_MS,
    active: timerActive,
    resetKey: `${state.turnNumber}-${state.currentPlayer}-${state.phase}-${showKickoff ? 'k' : 'g'}-${isBlocked ? 'b' : 'a'}`,
    onTimeout: () => {
      if (state.phase === 'turn-start') {
        dispatch({ type: 'DRAW' });
        setTimeout(() => dispatch({ type: 'TIMEOUT' }), 80);
      } else {
        dispatch({ type: 'TIMEOUT' });
      }
    },
  });

  // Guided demo progression
  useEffect(() => {
    if (guidedStep < 0) return;
    if (guidedStep === 0 && state.phase === 'drawn' && state.currentPlayer === 'p1') setGuidedStep(1);
    else if (guidedStep === 1 && (state.players.p1.attacksPerformed > 0 || state.turnNumber > 4)) setGuidedStep(2);
    else if (guidedStep === 2 && (state.players.p1.score > 0 || state.turnNumber > 12)) setGuidedStep(-1);
  }, [state.phase, state.currentPlayer, state.players.p1.attacksPerformed, state.players.p1.score, state.turnNumber]);

  const deckRemaining = selectors.remainingCards(state);
  const drawnExists = !!state.drawnCard;
  // Phase 3.8.2.1 — auto-clear the inline discard flash after 900 ms so the
  // card disappears and gameplay continues naturally. Keeping the timer here
  // (near consumers) so it lives with the flash state.
  useEffect(() => {
    if (!discardFlash) return undefined;
    const t = setTimeout(() => setDiscardFlash(null), 900);
    return () => clearTimeout(t);
  }, [discardFlash]);

  // Handlers
  const onDraw = () => {
    sounds.unlock();
    if (isBlocked) return;
    if (!isHumanTurn) return;
    if (state.phase !== 'turn-start') return;
    dispatch({ type: 'DRAW' });
  };
  const onPlaceSlot = ({ slotType, slotIndex }) => {
    if (isBlocked) return;
    if (!isHumanTurn) return;
    if (state.phase === 'joker-double-action') {
      // Joker DC place — any empty own slot (atk/def/goalkeeper).
      dispatch({ type: 'JOKER_DOUBLE_PLACE', slotType, slotIndex });
      return;
    }
    dispatch({ type: 'PLACE', slotType, slotIndex });
  };
  const onAttackTarget = (target) => {
    if (isBlocked) return;
    if (!isHumanTurn) return;
    if (state.phase === 'joker-double-action') {
      // Joker DC attack — must be opponent atk/def, NOT GK.
      if (target.slotType !== 'attack' && target.slotType !== 'defense') return;
      dispatch({ type: 'JOKER_DOUBLE_ATTACK', target });
      return;
    }
    dispatch({ type: 'ATTACK', target });
  };
  const onJokerShoot = () => {
    if (isBlocked) return;
    if (!isHumanTurn) return;
    if (state.phase !== 'joker-choice') return;
    dispatch({ type: 'JOKER_SHOOT' });
  };
  const onJokerDoubleChoose = () => {
    if (isBlocked) return;
    if (!isHumanTurn) return;
    if (state.phase !== 'joker-choice') return;
    dispatch({ type: 'JOKER_DOUBLE_CHOOSE' });
  };

  // Phase-based render
  //
  // Final-reveal guard (2026-02-13): keep the PenaltyView mounted while the
  // penalty-reveal hold is active even if the engine has flipped phase to
  // 'finished' on the deciding shot. Similarly, keep the board view (which
  // hosts joker/shot/dc reveal overlays) mounted while a board reveal is
  // pending. Only AFTER both holds expire is the match summary shown.
  //
  // PHASE 5.0B-4.4 P0 — Presentation-order invariant (bot mode mirror).
  // Also refuse to switch to PenaltyView while a regulation reveal is
  // still pending (`pendingBoardReveal` covers joker/shot/dc). Without
  // this gate the reveal from the final regulation action can survive
  // the switch to PenaltyView and re-render out of order after the
  // shootout unmounts it. Engine / rules UNCHANGED.
  const showPenaltyView = (state.phase === 'penalty' || (pendingPenaltyReveal && !!state.penalty))
    && !pendingBoardReveal;
  if (showPenaltyView) {
    return (
      <>
        <PenaltyView state={state} dispatch={dispatch} opponentName="Bot" penaltyRevealUntil={penaltyRevealUntil} />
        {canSkipPenaltyBot && (
          <button
            type="button"
            onClick={onTapSkipPenalty}
            aria-label="Skip penalty bot delay"
            data-testid="skip-bot-delay"
            className="absolute inset-0 z-[5] bg-transparent cursor-pointer"
            style={{ border: 'none', padding: 0 }}
          />
        )}
        {devEnabled && !showDev && (
          <button type="button" onClick={() => setShowDev(true)}
            className="absolute top-12 right-2 font-mono text-xs text-[hsl(var(--k-gold))] border border-[hsl(var(--k-gold)/0.5)] rounded px-2 py-1 bg-black/60 z-30"
            data-testid="dev-open-btn">DEV</button>
        )}
        {showDev && (
          <DevPanel state={state} onAction={(a) => dispatch(a)} onClose={() => setShowDev(false)} reveal={devReveal} onToggleReveal={() => setDevReveal((v) => !v)} />
        )}
      </>
    );
  }
  if (
    (state.phase === 'match-end' || state.phase === 'finished') &&
    state.endState &&
    !pendingBoardReveal &&
    !pendingPenaltyReveal &&
    Date.now() >= holdUntil
  ) {
    return (
      <MatchSummaryView state={state} onRematch={() => reset({ p2IsBot: true })} onHome={() => navigate('/home')} />
    );
  }

  // ----- BOARD VIEW -----
  const timerSec = Math.ceil(remaining / 1000);
  const timerTone = !timerActive ? 'default' : remaining <= 5000 ? 'red' : 'green';
  return (
    <div data-testid={TID.gameScreen} className="kickard-frame is-match pitch-bg kk-gameplay-root flex flex-col h-[100dvh] overflow-hidden" style={{ height: '100svh' }}>
      {/* TOP HUD — broadcast scoreboard (Phase 3B) */}
      <Scoreboard
        leftName={state.players.p1.name}
        rightName={state.players.p2.name}
        rightMeta="Bot · Balanced"
        leftScore={displayScores.p1}
        rightScore={displayScores.p2}
        leftScoreTestId={TID.scoreP1}
        rightScoreTestId={TID.scoreP2}
        activeSide={state.currentPlayer === 'p1' ? 'left' : 'right'}
        halfLabel={state.half === 1 ? '1ST HALF' : '2ND HALF'}
        halfTestId="half-label"
        deckCount={deckRemaining}
        deckTotal={42}
        deckTestId={TID.deckCount}
        timerLabel={timerActive ? `${timerSec}S` : '—'}
        timerTone={timerTone}
        timerTestId={TID.timer}
      />

      {/* BOARD — fills available height */}
      <div className="flex-1 flex items-center justify-center overflow-hidden relative">
        <PitchBackground />
        <GameBoard state={state} onPlaceSlot={onPlaceSlot} onAttackTarget={onAttackTarget} devReveal={devReveal} size="compact" />

        {/* HELP button — always-available glossary access. */}
        <button
          type="button"
          data-testid="tutorial-help-button"
          aria-label="Help / glossary"
          onClick={() => setShowHelp(true)}
          className="absolute top-2 left-2 z-20 w-9 h-9 rounded-full flex items-center justify-center border border-white/20 bg-black/55 backdrop-blur-sm active:scale-95 transition-transform"
          style={{ color: 'hsl(var(--k-gold-bright))' }}
        >
          <HelpCircle size={18} strokeWidth={2} />
        </button>

        {/* CENTRAL ACTION HUB — Draw button / drawn card / status, sitting on the midline */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex flex-col items-center justify-center gap-1 pointer-events-none" style={{ minHeight: 56 }}>
          {isHumanTurn && state.phase === 'turn-start' && !showKickoff && !isBlocked && (
            <motion.div
              key="draw"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ duration: 0.22 }}
              className="pointer-events-auto"
            >
              <MidfieldDrawBall
                onClick={onDraw}
                testId={TID.drawBtn}
                ariaLabel="Draw card"
              />
            </motion.div>
          )}
          {drawnExists && (
            <motion.div
              key="drawn"
              initial={{ scale: 0.6, opacity: 0, y: -10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col items-center gap-1"
            >
              <div className="font-heading text-[10px] tracking-[0.3em] text-white/55 uppercase">
                {isHumanTurn ? 'Your draw' : 'Bot drew'}
              </div>
              <div data-testid={TID.drawnCard} style={{ width: 'var(--kk-card-w)' }}>
                <PlayingCard
                  card={state.drawnCard}
                  highlight
                  small
                  faceDown={state.lastDrawnBy === 'p2' && !botCardRevealed}
                />
              </div>
            </motion.div>
          )}
          {/* Phase 3.8.2.5 — Inline no-valid-move discard flash. Absolutely
              positioned ABOVE the DRAW button anchor so the DRAW button
              never shifts / overlaps regardless of whether it's currently
              rendered. `bottom: 100%` pins the flash card just above the
              hub, leaving the DRAW button pinned to the midline. */}
          {!drawnExists && discardFlash && (
            <motion.div
              key={`dsc-flash-${discardFlash.id}`}
              data-testid="discard-flash-inline"
              initial={{ scale: 0.6, opacity: 0, y: -6 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-1"
              style={{ bottom: 'calc(100% + 8px)' }}
            >
              <div className="font-heading text-[10px] tracking-[0.3em] text-white/55 uppercase">
                {discardFlash.byP1 ? 'You drew — no move' : 'Bot drew — no move'}
              </div>
              <div style={{ width: 'var(--kk-card-w)' }} data-testid="discard-flash-card">
                <PlayingCard card={discardFlash.card} small />
              </div>
            </motion.div>
          )}
          {/* Joker V2 — SHOOT / DOUBLE CHANCE buttons. Shown only on the human's
              joker-choice turn. Bot makes its decision via the bot driver. */}
          {state.phase === 'joker-choice' && isHumanTurn && !isBlocked && (
            <motion.div
              key="joker-choice"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="flex flex-col items-stretch gap-2 mt-2 pointer-events-auto"
              data-testid="joker-choice-panel"
            >
              <button
                type="button"
                data-testid="joker-shoot-btn"
                onClick={onJokerShoot}
                className="btn-primary !text-sm !py-2 !px-5"
                style={{ minWidth: 180 }}
              >
                <span style={{ marginRight: 6 }}>⚽</span>SHOOT
              </button>
              <button
                type="button"
                data-testid="joker-double-btn"
                onClick={onJokerDoubleChoose}
                className="btn-secondary !text-sm !py-2 !px-5"
                style={{ minWidth: 180 }}
              >
                <span style={{ marginRight: 6 }}>🎲</span>DOUBLE CHANCE
              </button>
            </motion.div>
          )}
          {/* Joker DC follow-up hint */}
          {state.phase === 'joker-double-action' && isHumanTurn && !isBlocked && (
            <motion.div
              key="joker-dc-hint"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
              className="font-heading text-[10px] tracking-[0.22em] text-[hsl(var(--k-gold-bright))] mt-1 px-3 py-1 bg-black/55 rounded uppercase"
              data-testid="joker-dc-hint"
            >
              DOUBLE CHANCE — attack or place
            </motion.div>
          )}
          {!isHumanTurn && state.phase === 'turn-start' && !showKickoff && !isBlocked && (
            <div className="font-heading text-base tracking-[0.25em] text-white/65 px-3 py-1.5 bg-black/60 rounded">BOT THINKING…</div>
          )}
        </div>
      </div>

      {/* BOTTOM STATUS — Phase 5.0B FINAL kk-match-bottom shell. */}
      <div className="px-2 pt-1 pb-2 shrink-0">
        <MatchStatusBar
          turnLabel={isHumanTurn ? (drawnExists ? 'PICK AN ACTION' : 'YOUR TURN') : 'BOT TURN'}
          turnMuted={!isHumanTurn}
          turnTestId={TID.turnIndicator}
          helperText={drawnExists && isHumanTurn ? (
            validAttackTargets(state).length > 0 && validPlacements(state.players.p1, state.drawnCard).length > 0
              ? 'Green = valid attack or placement'
              : validAttackTargets(state).length > 0
                ? 'Green = valid attack'
                : 'Green = valid placement'
          ) : (isHumanTurn ? 'Select a card or draw' : null)}
          discardCount={state.discard.length}
          showExit={false}
        />
      </div>

      {/* Guided tip */}
      {guidedStep >= 0 && guidedStep < guidedSteps.length && (
        <div className="absolute left-3 right-3 bottom-16 pointer-events-none">
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-black/75 backdrop-blur-sm border border-[hsl(var(--k-gold)/0.6)] rounded-lg px-3 py-1.5 text-center">
            <span className="font-heading text-xs tracking-[0.18em] text-[hsl(var(--k-gold-bright))]">TIP</span>
            <div className="font-body text-xs text-white/85">{guidedSteps[guidedStep].text}</div>
          </motion.div>
        </div>
      )}

      {/* Toast block removed — see header comment. Gameplay no longer surfaces
          textual toasts; card animations and reveal sequences carry the info.
          NO-VALID-MOVE state is shown inline below. */}
      <AnimatePresence>
        {isHumanStuck && (
          <motion.div
            key="no-move"
            data-testid="no-valid-move-label"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute left-1/2 -translate-x-1/2 z-[15] pointer-events-none"
            style={{ top: 'calc(50% + 76px)' }}
          >
            <div className="font-heading text-xs tracking-[0.28em] text-[hsl(var(--k-red))] bg-black/70 border border-[hsl(var(--k-red)/0.55)] px-3 py-1 rounded uppercase">
              No valid move
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tap-anywhere to skip pacing pauses (bot thinking delay OR human's
          NO-VALID-MOVE auto-discard wait). Sits below reveal sequences so
          Joker/Shot/Kickoff/Dev are never skipped from here — those overlays
          have their own internal skip-tap. */}
      {canTapToSkip && (
        <button
          type="button"
          onClick={onTapSkip}
          aria-label="Skip pause"
          data-testid="skip-bot-delay"
          className="absolute inset-0 z-[5] bg-transparent cursor-pointer"
          style={{ border: 'none', padding: 0 }}
        />
      )}

      {/* Joker multi-step sequence */}
      {jokerSeq && (
        <JokerSequence sequence={jokerSeq} onDone={() => setJokerSeq(null)} />
      )}

      {/* Normal goalkeeper shot reveal sequence */}
      {shotSeq && (
        <ShotSequence sequence={shotSeq} onDone={() => setShotSeq(null)} />
      )}

      {/* Double-Chance attack/defense reveal sequence */}
      {dcSeq && (
        <DoubleChanceSequence sequence={dcSeq} onDone={() => setDcSeq(null)} />
      )}

      {/* HALFTIME overlay — shown when the first-half deck has just emptied.
          Displays scores, alternates the kicker, and auto-advances to the
          second half after HALFTIME_MS. Tap anywhere on the overlay to advance
          early (still respects a 500 ms minimum visibility). */}
      <AnimatePresence>
        {state.phase === 'halftime' && !pendingBoardReveal && !isBlocked && (
          <HalftimeOverlay
            scoreP1={state.players.p1.score}
            scoreP2={state.players.p2.score}
            nextStarter={state.firstHalfStarter === 'p1' ? 'p2' : 'p1'}
            onContinue={() => dispatch({ type: 'START_SECOND_HALF' })}
          />
        )}
      </AnimatePresence>

      {/* KICKOFF overlay */}
      <AnimatePresence>
        {showKickoff && (
          <motion.div key="kickoff" data-testid={TID.kickoffOverlay} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} className="absolute inset-0 z-40 bg-black/82 backdrop-blur-sm flex flex-col items-center justify-center">
            <motion.div initial={{ scale: 0.6, opacity: 0, letterSpacing: '0.8em' }} animate={{ scale: 1, opacity: 1, letterSpacing: '0.18em' }} transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }} className="font-heading uppercase" style={{ fontSize: 72, color: 'hsl(var(--k-gold-bright))', textShadow: '0 0 28px hsl(var(--k-gold) / 0.85)' }}>
              KICKOFF
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.4 }} className="font-body text-sm text-white/70 mt-3 tracking-[0.25em] uppercase">
              Formation dealt
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {devEnabled && !showDev && (
        <button type="button" onClick={() => setShowDev(true)} className="absolute top-12 right-2 font-mono text-xs text-[hsl(var(--k-gold))] border border-[hsl(var(--k-gold)/0.5)] rounded px-2 py-1 bg-black/60 z-20" data-testid="dev-open-btn">DEV</button>
      )}
      {showDev && (
        <DevPanel state={state} onAction={(a) => dispatch(a)} onClose={() => setShowDev(false)} reveal={devReveal} onToggleReveal={() => setDevReveal((v) => !v)} />
      )}
      {showPrimer && (
        <TutorialPrimer
          onSkip={() => {
            storage.setSettings({ tutorialPrimerSeen: true, tutorialSkipped: true });
            setShowPrimer(false);
            setTutorialSkipped(true);
          }}
          onStartMatch={() => {
            storage.setSettings({ tutorialPrimerSeen: true });
            setShowPrimer(false);
          }}
        />
      )}

      {/* Active coach mark (single visible at a time). */}
      <AnimatePresence>
        {activeCoach && !tutorialSkipped && !showPrimer && (
          <CoachMark
            mark={activeCoach}
            onNext={() => setActiveCoach(null)}
            onSkip={() => {
              storage.setSettings({ tutorialSkipped: true });
              setTutorialSkipped(true);
              setActiveCoach(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* HELP glossary (always-available via top-right HELP button). */}
      {showHelp && <HelpGlossary onClose={() => setShowHelp(false)} />}

      {/* JOKER explainer — one-shot when a Joker first appears on the
          player's turn. Uses the same body component as the glossary so
          the copy stays consistent everywhere. Mobile-safe (max-height,
          internal scroll, sticky close). */}
      {showJokerExplainer && !tutorialSkipped && (
        <div
          data-testid="tutorial-joker-modal"
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center px-3"
          onClick={() => setShowJokerExplainer(false)}
        >
          <div
            className="w-full max-w-[420px] rounded-xl border border-[hsl(var(--k-gold)/0.4)] bg-[hsl(220,12%,8%)] flex flex-col overflow-hidden"
            style={{ maxHeight: '85dvh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-white/10">
              <div className="font-heading text-lg tracking-[0.18em] uppercase text-white">Joker</div>
              <button
                data-testid="tutorial-joker-modal-close"
                aria-label="Close"
                className="w-9 h-9 rounded-full flex items-center justify-center text-white/75 hover:text-white border border-white/15 active:scale-95"
                onClick={() => setShowJokerExplainer(false)}
              >
                <X size={18} strokeWidth={2.25} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4" style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
              <JokerExplainerBody />
            </div>
          </div>
        </div>
      )}
      {showDcExplainer && !tutorialSkipped && (
        <div
          data-testid="tutorial-dc-modal"
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center px-3"
          onClick={() => setShowDcExplainer(false)}
        >
          <div
            className="w-full max-w-[420px] rounded-xl border border-[hsl(var(--k-gold)/0.4)] bg-[hsl(220,12%,8%)] flex flex-col overflow-hidden"
            style={{ maxHeight: '85dvh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-white/10">
              <div className="font-heading text-lg tracking-[0.18em] uppercase text-white">Double Chance</div>
              <button
                data-testid="tutorial-dc-modal-close"
                aria-label="Close"
                className="w-9 h-9 rounded-full flex items-center justify-center text-white/75 hover:text-white border border-white/15 active:scale-95"
                onClick={() => setShowDcExplainer(false)}
              >
                <X size={18} strokeWidth={2.25} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4" style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
              <DoubleChanceExplainerBody />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function suitGlyph(suit) {
  return suit === 'hearts' ? '♥' : suit === 'diamonds' ? '♦' : suit === 'clubs' ? '♣' : suit === 'spades' ? '♠' : '★';
}
